<template>
  <v-container fluid>
    <ConfirmDeleteDialog :item="itemToRemove"
      title="Remove transaction" @confirm-delete="remove" ref="confirmDialog"/>
    <v-list two-line class="transaction-list">
      <v-list-item v-for="(item, idx) in items" :key="item.id"
        v-observe-visibility="idx === items.length - 1 && nextPageUrl !== null ? loadMore : false"
        @click="$emit('preview-detail', item)" :class="'row-' + (idx % 2 === 0 ? 'even' : 'odd')">
        <v-list-item-content class="py-1">
          <v-container style="padding:0">
            <v-row no-gutters>
              <v-col cols="11">
                <v-list-item-title>
                  #{{ item.invoice_no }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <v-container style="padding:0">
                    <v-row no-gutters v-if="compact || $vuetify.breakpoint.smAndDown">
                      <v-col cols="5">{{ item.issue_time | dateParse('YYYY-MM-DD') | dateFormat('DD/MM/YYYY') }}</v-col>
                      <v-col cols="5">P.O. {{ item.po_number }}</v-col>
                      <v-col cols="2">
                        <span v-if="item.is_paid">${{ item.amount }}</span><span v-else>${{ item.amount_to_pay }}</span>
                      </v-col>
                      <v-col cols="10">Customer: {{ item.customer.name }}</v-col>
                    </v-row>
                    <v-row no-gutters v-else>
                      <v-col cols="3">{{ item.issue_time | dateParse('YYYY-MM-DD') | dateFormat('DD/MM/YYYY') }}</v-col>
                      <v-col cols="4">P.O. No: {{ item.po_number }}</v-col>
                      <v-col cols="4">Customer: {{ item.customer.name }}</v-col>
                      <v-col cols="1">
                        <span v-if="item.is_paid">${{ item.amount }}</span><span v-else>${{ item.amount_to_pay }}</span>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-list-item-subtitle>
              </v-col>
              <v-col cols="1">
                <v-container style="padding:0">
                  <v-row no-gutters>
                    <v-col cols="12">
                      <v-btn small text rounded color="success" @click.stop="$emit('load-detail', item)" v-if="item.isDraft">
                        <span><v-icon dense small>mdi-square-edit-outline</v-icon></span>
                        <span v-if="$vuetify.breakpoint.mdAndUp && !compact">Edit</span>
                      </v-btn>
                      <v-chip v-else outlined small class="ml-3" color="info">Sent</v-chip>
                    </v-col>
                    <v-col cols="12">
                      <v-menu v-model="item.payMenu" :close-on-content-click="false" :nudge-width="200" offset-x>
                        <template v-slot:activator="{ on }">
                          <v-btn small text color="success"  v-on="on">
                            <span><v-icon dense small>payment</v-icon></span>
                            <span v-if="$vuetify.breakpoint.mdAndUp && !compact">Pay</span>
                          </v-btn>
                        </template>
                        <v-card>
                          <v-card-text>
                            <v-text-field label="Payment amount"
                              v-model="item.payingAmount" :rules="rules"></v-text-field>
                            <v-text-field label="Notes" v-model="item.payingNotes"></v-text-field>
                          </v-card-text>
                          <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn text color="info" @click="addPayment(item)">OK</v-btn>
                            <v-btn text @click="item.payMenu = false">Cancel</v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-container>
              </v-col>
            </v-row>
          </v-container>
        </v-list-item-content>
      </v-list-item>
      <v-progress-linear v-if="loading" indeterminate></v-progress-linear>
    </v-list>
  </v-container>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import { VMoney } from 'v-money'
import ConfirmDeleteDialog from '@/components/dialog/ConfirmDeleteDialog'
import InvoiceService from '@/services/InvoiceService'

const SubModule = namespace('invoice')

@Component({
  components: {
    ConfirmDeleteDialog
  },
  directives: {
    money: VMoney
  }
})
export default class InvoiceList extends Vue {
  @SubModule.Getter service

  money = { prefix: '$', masked: true }
  loading = false
  page = 1
  nextPageUrl = null
  itemToRemove = null
  entityId = null
  compact = false
  items = []
  isPaid = false
  payMenus = []
  rules = [
    v => !!v || 'Required.',
    v => !v || /^\d(?:[\d.]*\d|)$/.test(v) || 'Amount must be valid'
  ]

  amountColor (item) {
    return item.trans_type === 'D' ? 'green--text' : 'orange--text'
  }

  loadMore (isVisible, entry) {
    if (isVisible) {
      this.page += 1
      this.load(this.entityId, true)
    }
  }

  load (entityId, append = false) {
    this.entityId = entityId
    this.loading = true
    if (!append) {
      this.page = 1
      this.items = []
    }
    this.service.list(entityId, this.isPaid, this.page)
      .then(resp => {
        const items = resp.data.results || resp.data
        items.map(item => {
          item.isDraft = item.is_draft
          item.payingAmount = item.amount_to_pay
        })
        if (append) {
          this.items = this.items.concat(items)
        } else {
          this.items = items
        }
        this.nextPageUrl = resp.data.next
      })
      .catch(err => {
        this.nextPageUrl = null
        console.error(err)
      })
      .finally(() => {
        this.loading = false
      })
  }

  setCompact (compact) {
    this.compact = compact
  }

  editItem (item) {
    const idx = this.cashbooks.indexOf(item)
    this.$refs.dialog.editItem(item, idx)
  }

  confirmRemove (item) {
    this.itemToRemove = item
    this.itemToRemove.name = item.description
    this.$refs.confirmDialog.open()
  }

  remove () {
    this.service.delete(this.itemToRemove.id)
      .then(() => {
        this.load(this.entityId)
        this.$emit('load-balance')
      })
      .finally(() => {
        this.itemToRemove = null
        this.$refs.confirmDialog.close()
      })
  }

  open (idx) {
    this.$refs.dialog.open()
  }

  save (data) {
    const idx = data.idx
    if (idx > -1) {
      Object.assign(this.cashbooks[idx], data.item)
    } else {
      this.cashbooks.push(data.item)
    }
  }

  getCodeColor (code) {
    if (code >= 11 && code < 20) {
      return 'blue'
    }
    if (code >= 20 && code <= 30) {
      return 'green'
    }
    if (code > 30) {
      return 'purple'
    }
    return 'black'
  }

  getCodeText (code) {
    if (code >= 11 && code < 20) {
      return 'D'
    }
    if (code >= 20 && code <= 30) {
      return 'G'
    }
    if (code > 30) {
      return 'W'
    }
    return ' '
  }

  setPaid (paid) {
    this.isPaid = paid
    this.load(this.entityId)
  }

  addPayment (item) {
    InvoiceService.addPayment(item.id, { amount: item.payingAmount })
      .then(resp => {
        this.$emit('payment-added', { 'color': 'success', 'detail': 'Payment added!' })
      })
      .catch(err => {
        console.log('error', err)
        this.$emit('payment-added', { 'color': 'error', 'detail': err })
      })
      .finally(() => {
        item.payMenu = false
      })
  }
}
</script>

<style lang="scss">
.transaction-list {
  border-top: 1px solid rgba(0,0,128,.2) !important;
  .v-list-item__content {
    padding: 8px 0 0 0;
  }
  .row-even {
    border-bottom: 1px solid rgb(0, 0, 128, .2);
    background-color: rgba(0, 60, 128, .1);
  }
  .row-odd {
    border-bottom: 1px solid rgb(0, 0, 128, .2);
    background-color: white;
  }
}
.list-mask {
  position: fixed;
  left: 0; right: 50%;
  top: 64px; bottom: 60px;
  z-index:10;
  background-color: black;
  opacity: 0.25;
}
</style>
